import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { DeselectionService, PeriodService, ToastService } from '@app/core';
import {
    DateHelper,
    DatePick,
    Deselection,
    DeselectionState,
    Emit,
    Holiday,
    Period,
    Settings
} from '@app/shared';


@Component({
    selector: 'app-deselection',
    templateUrl: './deselection.component.html',
    styleUrls: ['./deselection.component.css']
})
export class DeselectionComponent implements OnInit {

    settings: Settings;
    period: Period;
    private _allowed_deselections: number;
    private _disabledSave = false;
    private _holidays: Holiday[];
    private _months: DeselectionState[][];
    private _remainingDeselections = 0;

    constructor(
        private deselectionService: DeselectionService,
        private periodService: PeriodService,
        private route: ActivatedRoute,
        private router: Router,
        private toast: ToastService,
    ) { }

    ngOnInit() {
        this.route.data.subscribe(
            data => {
                this.period = data.period;
                this.settings = data.settings;
                this._holidays = data.holidays;
                this._allowed_deselections = this.settings.allowed_deselections;
                this.prepare(data.deselections);
            }
        );
    }

    get months() {
        return this._months;
    }

    get disabledSave() {
        return this._disabledSave;
    }

    get remainingDeselections() {
        return this._remainingDeselections;
    }

    save() {
        this._disabledSave = true;
        this.calculateRemainingDeselections();
        if (this.remainingDeselections < 0) {
            this.toast.error('Fejl: For mange fravalg');
            this._disabledSave = false;
            return;
        }
        const states: DeselectionState[] = Array.prototype.concat(...this._months);
        const deselections = states.map(d => d.data);
        this.deselectionService.save(this.period, deselections).subscribe(
            data => { },
            error => {
                console.error('Error saving deselections', error);
                this._disabledSave = false;
                if (Array.isArray(error.error) && error.error.length === 1) {
                    this.toast.error('Fejl: ' + error.error[0]);
                } else {
                    this.toast.error('Fejl: Fravalg ikke gemt');
                }
            },
            () => {
                this._disabledSave = false;
                this.toast.success('Fravalg gemt');
                this.router.navigate(['deselection-list']);
            }
        );
    }

    listener(event: Emit) {
        if (event.type === 'update') {
            this.calculateRemainingDeselections();
        }
    }

    private mergeDeselection(deselection: Deselection, deselections: Map<string, DeselectionState[]>) {
        const datePick = DateHelper.getDatePick(deselection.date);
        const key = DateHelper.getDatePickKey(datePick);
        const states = deselections.get(key);
        const index = states.findIndex(s => s.data.date === deselection.date);
        states.splice(index, 1, new DeselectionState(deselection, this._holidays));
    }

    private prepare(loadedDeselections: Deselection[]) {
        this._disabledSave = this.period.is_locked;
        const months = DateHelper.getMonths(this.period);
        const deselections = this.generateMonths(this.period, months);
        loadedDeselections.forEach(
            d => this.mergeDeselection(d, deselections));
        this._months = Array.from(deselections.values());
        this.calculateRemainingDeselections();
    }

    private generateMonths(period: Period, months: DatePick[]) {
        const map = new Map<string, DeselectionState[]>();
        months.forEach(month => {
            const key = DateHelper.getDatePickKey(month);
            map.set(key, this.generateMonth(period, month));
        });
        return map;
    }

    private generateMonth(period: Period, month: DatePick) {
        const days: DeselectionState[] = [];
        const startDay = this.getStartDay(period, month);
        const daysInMonth = this.getDaysInMonth(period, month) + 1;
        for (let i = startDay; i < daysInMonth; i++) {
            const deselection = new Deselection();
            deselection.period = period.id;
            deselection.date = DateHelper.getDateString(new Date(month.year, month.month, i));
            days.push(new DeselectionState(deselection, this._holidays));
        }
        return days;
    }

    private getStartDay(period: Period, month: DatePick) {
        const start = DateHelper.getDatePick(period.start);
        if (month.month === start.month && month.year === start.year) {
            return start.day;
        }
        return 1;
    }

    private getDaysInMonth(period: Period, month: DatePick) {
        const end = DateHelper.getDatePick(period.end);
        if (month.month === end.month && month.year === end.year) {
            return end.day;
        }
        return DateHelper.getDaysInMonth(month);
    }

    private calculateRemainingDeselections() {
        const count = this.countDeselections();
        this._remainingDeselections = this._allowed_deselections - count;
    }

    private countDeselections() {
        let count = 0;
        this._months.forEach(month => {
            month.forEach(d => count += Number(d.data.day) + Number(d.data.evening));
        });
        return count;
    }

}
